import React from 'react';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
    getCallRecordDisplayTimestamp,
    showBankidVerification,
} from 'flow/utils/call-record-utils';
import ContactTitle from './ContactTitle';
import Icon from 'flow/components/ui-components/Icon';
import { useRowTabIndex } from 'flow/components/ui-components/row-tab-stop';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { PageType } from 'flow/components/call-history-v2/types';
import { useCallRecordWithCtiContactByKey } from 'flow/hooks/useCallRecordWithCtiContact';

interface Props {
    callRecordKey: CallRecordKey;
    contactFullName?: string;
    callIsMissedCall: boolean;
    hideTimestamp?: boolean;
    pageType?: PageType;
    /** Wraps the name in a link to this path; see CallRecordItemV2. */
    linkTo?: string;
    onLinkClick?: () => void;
    /** Makes the name a button that opens the call; see CallRecordItemV2. */
    onOpen?: () => void;
}

const TitleRow = ({
    callRecordKey,
    contactFullName,
    callIsMissedCall,
    hideTimestamp,
    pageType,
    linkTo,
    onLinkClick,
    onOpen,
}: Props) => {
    const callRecord = useCallRecordWithCtiContactByKey(callRecordKey);
    const rowTabIndex = useRowTabIndex();

    if (!callRecord) return null;

    const title = (
        <ContactTitle
            contact={callRecord.get('contact')}
            contactFullName={contactFullName}
            number={callRecord.get('number')}
        />
    );

    // The card's root is relative, so the link or button covers the card.
    const renderTitle = () => {
        if (linkTo) {
            return (
                <NavLink
                    to={linkTo}
                    onClick={onLinkClick}
                    tabIndex={rowTabIndex}
                    className="stretched-control flex min-w-0"
                >
                    {title}
                </NavLink>
            );
        }
        if (onOpen) {
            return (
                <button
                    type="button"
                    onClick={onOpen}
                    tabIndex={rowTabIndex}
                    className="stretched-control flex min-w-0 cursor-pointer text-start"
                >
                    {title}
                </button>
            );
        }
        return title;
    };

    return (
        <div className="flex min-h-6 items-center justify-between gap-x-2">
            <div
                className={twMerge(
                    'flex min-w-0 items-center gap-1 font-bold antialiased',
                    callIsMissedCall ? 'text-red' : 'text-gray-800',
                )}
            >
                {renderTitle()}
            </div>

            {/* Time (visible by default, hidden on hover or focus) */}
            <div
                className={twMerge(
                    'items-center gap-1 text-sm whitespace-nowrap text-gray-500 antialiased',
                    hideTimestamp
                        ? 'hidden'
                        : 'group-focus-visible-within:hidden flex group-hover:hidden',
                )}
            >
                {showBankidVerification(callRecord) && (
                    <Icon iconKey="gppGood" size="1rem" />
                )}
                {moment(
                    getCallRecordDisplayTimestamp(callRecord, pageType),
                ).format('HH:mm')}
            </div>
        </div>
    );
};

export default TitleRow;
